import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatApiError, API } from "@/lib/api";
import { log } from "@/lib/log";
import { toast } from "sonner";
import { Download, CheckCircle2, Landmark, Printer, Send } from "lucide-react";

const MARK_PAID_POLL_MS = 1500;

export default function PaymentQueue() {
  const [tab, setTab] = useState("ach");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("pending");
  const [ppConfig, setPpConfig] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/payments/queue?status=${status}`);
    setData(data);
    setSelected(new Set());
  }, [status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/paypal/config");
        setPpConfig(data);
      } catch (err) {
        // Non-admins get 403 here — silent fallback keeps the PayPal button hidden.
        log.warn("PayPal config check failed:", err?.message || err);
      }
    })();
  }, []);

  let items;
  if (tab === "ach") items = data?.ach || [];
  else if (tab === "check") items = data?.checks || [];
  else items = data?.paypal || [];

  const allSelected = items.length > 0 && items.every((it) => selected.has(it.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };
  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const selectedTotal = useMemo(
    () => items.filter((i) => selected.has(i.id)).reduce((a, b) => a + b.net_pay, 0),
    [items, selected]
  );

  const markPaid = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} payment(s) as paid?`)) return;
    setBusy(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const ref = tab === "ach" ? `ACH-BATCH-${today}` : `CHECKS-DISTRIBUTED-${today}`;
      const { data } = await api.post("/payments/mark-paid", { stub_ids: Array.from(selected), reference: ref });
      toast.success(`${data.updated} payment(s) marked paid`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const sendPaypal = async () => {
    if (selected.size === 0) return;
    if (!ppConfig?.configured) {
      toast.error("PayPal is not configured. Add PAYPAL_CLIENT_ID and PAYPAL_CLIENT_SECRET to backend/.env, then restart the backend.");
      return;
    }
    if (!window.confirm(`Send ${selected.size} PayPal payout(s) totaling ${fmtMoney(selectedTotal)}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/paypal/payout", { stub_ids: Array.from(selected) });
      toast.success(`PayPal batch ${data.payout_batch_id} · ${data.item_count} item(s) queued`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">§ DISBURSEMENTS</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Payment Queue</h1>
          <p className="text-sm text-muted-foreground mt-2">Queue direct-deposit ACH batches, send PayPal payouts, and mark printed checks as distributed.</p>
        </div>
        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
          {["pending", "paid", "all"].map((s) => (
            <button key={s} data-testid={`filter-status-${s}`} onClick={() => setStatus(s)} className={`border-2 border-ink px-3 py-1.5 ${status === s ? "bg-ink text-white" : "bg-white"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <Card label="ACH · Count" val={data?.totals?.ach_count ?? 0} money={false} icon={Landmark} />
        <Card label="ACH · Total" val={data?.totals?.ach_total ?? 0} color="text-good" />
        <Card label="PayPal · Count" val={data?.totals?.paypal_count ?? 0} money={false} icon={Send} />
        <Card label="PayPal · Total" val={data?.totals?.paypal_total ?? 0} color="text-good" />
        <Card label="Check · Count" val={data?.totals?.check_count ?? 0} money={false} icon={Printer} />
        <Card label="Check · Total" val={data?.totals?.check_total ?? 0} />
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex border-2 border-ink">
          <button data-testid="tab-ach" onClick={() => { setTab("ach"); setSelected(new Set()); }} className={`px-5 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2 ${tab === "ach" ? "bg-ink text-white" : ""}`}>
            <Landmark className="w-4 h-4" /> ACH ({data?.totals?.ach_count ?? 0})
          </button>
          <button data-testid="tab-paypal" onClick={() => { setTab("paypal"); setSelected(new Set()); }} className={`px-5 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2 border-l-2 border-ink ${tab === "paypal" ? "bg-ink text-white" : ""}`}>
            <Send className="w-4 h-4" /> PayPal ({data?.totals?.paypal_count ?? 0})
          </button>
          <button data-testid="tab-check" onClick={() => { setTab("check"); setSelected(new Set()); }} className={`px-5 py-2 font-mono text-xs uppercase tracking-widest flex items-center gap-2 border-l-2 border-ink ${tab === "check" ? "bg-ink text-white" : ""}`}>
            <Printer className="w-4 h-4" /> Checks ({data?.totals?.check_count ?? 0})
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tab === "ach" && status === "pending" && (
            <a data-testid="download-ach-csv" href={`${API}/payments/ach-export.csv`} target="_blank" rel="noreferrer" className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-secondary">
              <Download className="w-4 h-4" /> Download ACH Batch CSV
            </a>
          )}
          {tab === "paypal" && status === "pending" && (
            <button
              data-testid="paypal-send-btn"
              onClick={sendPaypal}
              disabled={busy || selected.size === 0}
              className="border-2 border-[#003087] bg-[#0070ba] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#003087]"
              title={ppConfig?.configured ? `PayPal ${ppConfig?.mode} mode` : "PayPal not configured — add creds to backend/.env"}
            >
              <Send className="w-4 h-4" /> {busy ? "Sending…" : `Send PayPal Payout${selected.size > 0 ? ` · ${fmtMoney(selectedTotal)}` : ""}`}
            </button>
          )}
          <button
            data-testid="mark-paid-btn"
            onClick={markPaid}
            disabled={busy || selected.size === 0}
            className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle2 className="w-4 h-4" /> {busy ? "Working…" : `Mark ${selected.size} Paid${selected.size > 0 ? ` · ${fmtMoney(selectedTotal)}` : ""}`}
          </button>
        </div>
      </div>

      {tab === "paypal" && !ppConfig?.configured && (
        <div data-testid="paypal-not-configured" className="border-2 border-warn bg-warn/10 p-4 mb-4 font-mono text-xs">
          <b>PayPal not configured.</b> Add <code>PAYPAL_CLIENT_ID</code> and <code>PAYPAL_CLIENT_SECRET</code> to <code>backend/.env</code> (get them from developer.paypal.com → My Apps &amp; Credentials → Sandbox), then restart the backend. PayPal Payouts sends money directly to a recipient's email — approval required for live mode.
        </div>
      )}

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="queue-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="px-3 py-3 w-8">
                <input type="checkbox" data-testid="toggle-all" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-white" disabled={items.length === 0} />
              </th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Employee</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Pay Date</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">{
                tab === "ach" ? "Routing · Account"
                : tab === "paypal" ? "PayPal Email · Batch"
                : "Check #"
              }</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Status</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Net</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (() => {
              const bucketLabel = tab === "ach" ? "ACH payments" : tab === "paypal" ? "PayPal payouts" : "checks";
              return <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No {bucketLabel} in this queue.</td></tr>;
            })()}
            {items.map((it, i) => (
              <tr key={it.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`queue-row-${it.id}`}>
                <td className="px-3 py-3 border-t border-ink/20">
                  <input type="checkbox" data-testid={`select-${it.id}`} checked={selected.has(it.id)} onChange={() => toggle(it.id)} disabled={it.payment_status === "paid"} className="w-4 h-4" />
                </td>
                <td className="px-3 py-3 border-t border-ink/20">
                  <div className="font-semibold">{it.employee_name}</div>
                  <div className="text-xs text-muted-foreground">{it.email}</div>
                </td>
                <td className="px-3 py-3 border-t border-ink/20">{it.pay_date}</td>
                <td className="px-3 py-3 border-t border-ink/20">
                  <PaymentIdentifierCell tab={tab} item={it} />
                </td>
                <td className="px-3 py-3 border-t border-ink/20">
                  {it.payment_status === "paid"
                    ? <span className="px-2 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest">Paid</span>
                    : <span className="px-2 py-0.5 bg-warn text-ink text-[10px] uppercase tracking-widest">Pending</span>}
                </td>
                <td className="px-3 py-3 border-t border-ink/20 text-right font-black">{fmtMoney(it.net_pay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, val, color = "", money = true, icon: Icon }) {
  return (
    <div className="hard-card p-4 relative">
      <div className="overline mb-1 flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</div>
      <div className={`font-mono font-black text-2xl ${color}`}>{money ? fmtMoney(val) : val}</div>
    </div>
  );
}

function PaymentIdentifierCell({ tab, item }) {
  if (tab === "ach") {
    return (
      <span className="whitespace-nowrap">
        {item.bank_routing} · ••••{item.bank_account_last4}
        <span className="uppercase text-[10px] text-muted-foreground ml-1">{item.account_type}</span>
      </span>
    );
  }
  if (tab === "paypal") {
    return (
      <span className="whitespace-nowrap">
        {item.paypal_email}
        {item.paypal_batch_id && (
          <span className="ml-2 text-[10px] text-muted-foreground uppercase">Batch {item.paypal_batch_id.slice(0, 10)}…</span>
        )}
      </span>
    );
  }
  return <span>#{item.check_number}</span>;
}

// Poll interval constant referenced by other flows/tests.
export { MARK_PAID_POLL_MS };
